import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useStyling } from '../../contexts/StylingContext';
import { useTheme } from '../../contexts/ThemeContext';
import { APP_VERSION } from '../../lib/appVersion';
import { isFullBleedRoute } from '../../lib/fullBleedRoutes';
import {
  LayoutDashboard,
  Building2,
  Laptop,
  Ticket,
  Settings,
  LogOut,
  ChevronDown,
  ChevronRight,
  User,
  Menu,
  X,
  FolderKanban,
  Coins,
  CalendarClock,
  Map,
  ScrollText,
  Sun,
  Moon,
  Users,
  ListChecks,
  AlertTriangle,
  FileWarning,
  Settings2,
  Mail,
  Percent,
  Package,
  Boxes,
  Contact,
  RefreshCw,
  ClipboardList,
  Calendar,
  Monitor,
  FileText,
  Plug,
  ShoppingCart,
  MapPin,
  HardDrive,
  Timer,
  ReceiptText,
  Stethoscope,
  History,
  Shield,
  Palette,
  Activity,
  LayoutGrid,
  Truck,
  BookOpen,
  BarChart3,
  ShieldCheck,
  Sparkles,
  Bell,
  Layers,
  ShieldAlert,
  Zap,
  Globe,
  Link2,
  ArrowLeft,
  Headset,
  Search,
  BellRing,
  FlaskConical,
  ClipboardCheck,
  Gauge,
  Pin,
  PinOff,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Suspense, useState, useEffect, useRef } from 'react';
import { RouteFallback } from '../shared/RouteFallback';
import { cn } from '../../lib/utils';
import NotificationBell from '../notifications/NotificationBell';
import { SystemStatusIndicator } from '../system/SystemStatusIndicator';
import { ConnectionStatusIndicator } from '../system/ConnectionStatusIndicator';

// ─── Main navigation groups ──────────────────────────────
// Each item may carry a `gate` ({resource, action}) that is checked against the
// permission system. Groups may carry a group-level `gate`. Items/groups with no
// gate are visible to everyone. Active/selected highlighting is handled by NavItem.
const mainGroups = [
  {
    key: 'delivery',
    label: 'Delivery',
    icon: Headset,
    defaultOpen: true,
    items: [
      { to: '/tickets', icon: Ticket, label: 'Tickets', excludeSearch: { key: 'type', value: 'internal' } },
      { to: '/scheduler', icon: Calendar, label: 'Scheduler', gate: { resource: 'scheduling', action: 'view' } },
      { to: '/site-visits', icon: MapPin, label: 'Site Visits', gate: { resource: 'scheduling', action: 'view' } },
      { to: '/projects', icon: FolderKanban, label: 'Projects', gate: { resource: 'projects', action: 'view' } },
      { to: '/my-tasks', icon: ListChecks, label: 'My Tasks', gate: { resource: 'projects', action: 'view' } },
      { to: '/clients', icon: Building2, label: 'Clients', gate: { resource: 'clients', action: 'view' } },
      { to: '/contacts', icon: Contact, label: 'Contacts', gate: { resource: 'clients', action: 'view' } },
      { to: '/kb', icon: BookOpen, label: 'Knowledge Base', gate: { resource: 'kb', action: 'view' }, excludePrefix: '/kb/workflows' },
      { to: '/kb/workflows', icon: Layers, label: 'Workflows', gate: { resource: 'kb', action: 'view' } },
      { to: '/compliance/byod', icon: ShieldCheck, label: 'Compliance', gate: { resource: 'compliance', action: 'view' } },
      { to: '/roadmap', icon: Map, label: 'Roadmap' },
    ],
  },
  {
    key: 'assets',
    label: 'Assets & Inventory',
    icon: Boxes,
    defaultOpen: true,
    items: [
      { to: '/assets', icon: Laptop, label: 'All Assets', exact: true, gate: { resource: 'assets', action: 'view' } },
      { to: '/assets/services-overview', icon: Layers, label: 'Services Overview', gate: { resource: 'assets', action: 'view' } },
      { to: '/stock', icon: Boxes, label: 'Stock', gate: { resource: 'stock', action: 'view' } },
      { to: '/storage-layouts', icon: LayoutGrid, label: 'Storage Layouts', gate: { resource: 'stock', action: 'view' } },
      { to: '/orders/requests', icon: ClipboardList, label: 'Order Requests', gate: { resource: 'tickets', action: 'create' } },
      { to: '/orders', icon: ShoppingCart, label: 'Purchase Orders', exact: true, gate: { resource: 'billing', action: 'view' } },
      { to: '/scheduled-tasks', icon: CalendarClock, label: 'Scheduled Tasks', gate: { resource: 'scheduling', action: 'view' } },
    ],
  },
  {
    key: 'internal',
    label: 'Internal',
    icon: ClipboardList,
    defaultOpen: true,
    gate: { resource: 'internal', action: 'view' },
    items: [
      { to: '/tickets?type=internal', icon: ClipboardList, label: 'Internal Tickets', matchSearch: true, matchPrefix: '/internal/tickets' },
      { to: '/internal/projects', icon: FolderKanban, label: 'Internal Projects' },
      { to: '/internal/assets', icon: Laptop, label: 'Internal Assets' },
      { to: '/internal/timesheet', icon: Timer, label: 'Timesheet' },
    ],
  },
  {
    key: 'services',
    label: 'Services',
    icon: Package,
    defaultOpen: false,
    gate: { resource: 'billing', action: 'view' },
    items: [
      { to: '/services', icon: Package, label: 'Service Catalog', exact: true },
      { to: '/billing/renewals', icon: RefreshCw, label: 'Service Renewals' },
    ],
  },
  {
    key: 'billing',
    label: 'Billing',
    icon: Coins,
    defaultOpen: false,
    gate: { resource: 'billing', action: 'view' },
    items: [
      { to: '/billing', icon: Coins, label: 'Overview', exact: true },
      { to: '/billing/review', icon: ClipboardList, label: 'Review Queue' },
      { to: '/billing/retainers', icon: Gauge, label: 'Retainer Burn-down' },
      { to: '/billing/pending-time', icon: Timer, label: 'Pending Time' },
      { to: '/billing/pending-items', icon: Package, label: 'Pending Items' },
      { to: '/billing/courier', icon: Truck, label: 'Courier Billing' },
      { to: '/billing/time-report', icon: FileText, label: 'Time Report' },
      { to: '/billing/entities', icon: Building2, label: 'Entity Dashboard' },
      { to: '/billing/statements', icon: ReceiptText, label: 'Entity Statements' },
      { to: '/billing/dry-run', icon: FlaskConical, label: 'Dry Run' },
      { to: '/billing/included-value', icon: Coins, label: 'Included Value' },
      { to: '/billing/contracts-report', icon: ListChecks, label: 'Contracts Report' },
      { to: '/billing/zero-priced', icon: AlertTriangle, label: 'Priced at £0' },
      { to: '/billing/rate-history', icon: History, label: 'Rate History' },
      { to: '/billing/missing-lines', icon: FileWarning, label: 'Invoices with no lines' },
      { to: '/billing/service-summaries', icon: FileText, label: 'Service Summaries' },
      { to: '/billing/diagnostics', icon: Stethoscope, label: 'Diagnostics & Audit' },
    ],
  },
];

// ─── Settings destination categories ─────────────────────
const settingsCategories = [
  {
    key: 'mine',
    label: 'Mine',
    icon: Bell,
    items: [
      { to: '/settings/my-notifications', icon: Bell, label: 'My Notifications' },
    ],
  },
  {
    key: 'people',
    label: 'People & Access',
    icon: Users,
    gateAny: [
      { resource: 'admin', action: 'manageUsers' },
      { resource: 'admin', action: 'managePortal' },
    ],
    items: [
      { to: '/settings/users', icon: Users, label: 'Users', gate: { resource: 'admin', action: 'manageUsers' } },
      { to: '/settings/teams', icon: Users, label: 'Teams', gate: { resource: 'admin', action: 'manageUsers' } },
      { to: '/settings/roles', icon: Shield, label: 'Roles', gate: { resource: 'admin', action: 'manageUsers' } },
      { to: '/settings/portal-users', icon: Globe, label: 'Portal Users', gate: { resource: 'admin', action: 'managePortal' } },
      { to: '/settings/portal', icon: Link2, label: 'Portal Magic Links', gate: { resource: 'admin', action: 'managePortal' } },
    ],
  },
  {
    key: 'automation',
    label: 'Automation',
    icon: Zap,
    items: [
      { to: '/settings/workflows', icon: Zap, label: 'Workflows', gate: { resource: 'admin', action: 'manageWorkflows' } },
      { to: '/settings/note-templates', icon: FileText, label: 'Note Templates', gate: { resource: 'admin', action: 'manageNoteTemplates' } },
      { to: '/settings/notifications', icon: Bell, label: 'Notifications', gate: { resource: 'admin', action: 'manageNotifications' } },
      { to: '/settings/alerts', icon: BellRing, label: 'Alerts', gate: { resource: 'admin', action: 'systemSettings' } },
      { to: '/settings/completion-rules', icon: ShieldCheck, label: 'Completion Rules', gate: { resource: 'admin', action: 'manageCompletionRules' } },
      { to: '/settings/ai', icon: Sparkles, label: 'AI Settings', gate: { resource: 'admin', action: 'manageAiSettings' } },
    ],
  },
  {
    key: 'values',
    label: 'Values',
    icon: ListChecks,
    items: [
      { to: '/settings/values', icon: ListChecks, label: 'Values', gate: { resource: 'admin', action: 'manageValues' } },
      { to: '/settings/asset-config', icon: Laptop, label: 'Asset Config', gate: { resource: 'admin', action: 'manageAssetConfig' } },
      { to: '/settings/client-fields', icon: FileText, label: 'Client Fields', gate: { resource: 'admin', action: 'manageClientFields' } },
      { to: '/settings/storage-locations', icon: MapPin, label: 'Storage Locations', gate: { resource: 'admin', action: 'manageStorageLocations' } },
      { to: '/settings/tax-rates', icon: Percent, label: 'Tax Rates', gate: { resource: 'admin', action: 'manageTaxRates' } },
    ],
  },
  {
    key: 'operations',
    label: 'Operations',
    icon: Activity,
    items: [
      { to: '/settings/sla', icon: Timer, label: 'SLA', gate: { resource: 'admin', action: 'manageSla' } },
      { to: '/settings/health-checks', icon: Stethoscope, label: 'Health Checks', gate: { resource: 'admin', action: 'manageHealthChecks' } },
      { to: '/settings/storage-usage', icon: HardDrive, label: 'Storage Usage', gate: { resource: 'admin', action: 'viewStorageUsage' } },
      { to: '/settings/service-review', icon: ClipboardCheck, label: 'Service Review', gate: { resource: 'admin', action: 'systemSettings' } },
      { to: '/background-task-logs', icon: Activity, label: 'Background Tasks', gate: { resource: 'admin', action: 'viewBackgroundTasks' } },
    ],
  },
  {
    key: 'integrations',
    label: 'Integrations',
    icon: Plug,
    items: [
      { to: '/integrations', icon: LayoutDashboard, label: 'Dashboard', gate: { resource: 'admin', action: 'viewIntegrationHealth' } },
      { to: '/settings/email', icon: Mail, label: 'Email', gate: { resource: 'admin', action: 'manageEmail' } },
      { to: '/settings/ninjaone', icon: Monitor, label: 'NinjaOne', gate: { resource: 'admin', action: 'manageNinjaOne' } },
      { to: '/settings/xero', icon: FileText, label: 'Xero', gate: { resource: 'admin', action: 'manageXero' } },
      { to: '/settings/m365', icon: Shield, label: 'M365', gate: { resource: 'admin', action: 'manageM365' } },
    ],
  },
  {
    key: 'logs',
    label: 'Logs',
    icon: FileText,
    items: [
      { to: '/security-events', icon: ShieldAlert, label: 'Security Events', gate: { resource: 'admin', action: 'viewAuditLog' } },
      { to: '/audit', icon: ScrollText, label: 'Audit Logs', gate: { resource: 'admin', action: 'viewAuditLog' } },
      { to: '/sync-logs', icon: RefreshCw, label: 'Sync Logs', gate: { resource: 'admin', action: 'viewSyncLogs' } },
      { to: '/email-logs', icon: Mail, label: 'Email Logs', gate: { resource: 'admin', action: 'viewAuditLog' } },
      { to: '/log-settings', icon: Settings, label: 'Log Settings', gate: { resource: 'admin', action: 'manageLogSettings' } },
    ],
  },
  {
    key: 'brand',
    label: 'Brand & General',
    icon: Palette,
    items: [
      { to: '/settings/company', icon: Building2, label: 'Company', gate: { resource: 'admin', action: 'manageCompany' } },
      { to: '/settings/styling', icon: Palette, label: 'App Styling', gate: { resource: 'admin', action: 'manageStyling' } },
      { to: '/settings/system', icon: Settings2, label: 'System', gate: { resource: 'admin', action: 'manageSystem' } },
    ],
  },
];

// Routes that live in the Settings destination but are NOT under /settings.
const SETTINGS_EXTRA_PATHS = ['/audit', '/email-logs', '/sync-logs', '/security-events', '/log-settings', '/integrations', '/background-task-logs'];

function isSettingsPath(pathname) {
  if (pathname === '/settings/changelog') return false; // "What's New" is an app page
  if (pathname.startsWith('/settings')) return true;
  // startsWith (not exact) so future sub-routes like /audit/:id still engage Settings mode
  return SETTINGS_EXTRA_PATHS.some((p) => pathname === p || pathname.startsWith(p + '/'));
}

function loadOpen(key, defaults) {
  try {
    const stored = JSON.parse(window.localStorage.getItem(key) || '{}');
    return { ...defaults, ...stored };
  } catch {
    return { ...defaults };
  }
}
function saveOpen(key, val) {
  try { window.localStorage.setItem(key, JSON.stringify(val)); } catch { /* ignore */ }
}

// NavItem — leaf link. Highlighting logic unchanged from the original.
const NavItem = ({ to, icon: Icon, label, exact, indent, onClick, matchSearch, excludeSearch, matchPrefix, excludePrefix }) => (
  <NavLink
    to={to}
    end={exact}
    className={({ isActive }) => {
      if (matchPrefix && window.location.pathname.startsWith(matchPrefix)) {
        return cn('nav-item', 'active', indent && 'pl-5');
      }
      if (excludePrefix && window.location.pathname.startsWith(excludePrefix)) {
        return cn('nav-item', indent && 'pl-5');
      }
      if (matchSearch) {
        const currentUrl = window.location.pathname + window.location.search;
        const isMatch = currentUrl === to || currentUrl.startsWith(to + '&');
        return cn('nav-item', isMatch && 'active', indent && 'pl-5');
      }
      if (excludeSearch && isActive) {
        const params = new URLSearchParams(window.location.search);
        if (params.get(excludeSearch.key) === excludeSearch.value) {
          return cn('nav-item', indent && 'pl-5');
        }
      }
      return cn('nav-item', isActive && 'active', indent && 'pl-5');
    }}
    onClick={onClick}
    data-testid={`nav-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
    title={label}
  >
    <Icon className="h-4 w-4 shrink-0" />
    <span className="rail-text">{label}</span>
  </NavLink>
);

// Collapsible group used by BOTH the main nav and the Settings categories.
const NavGroup = ({ icon: Icon, label, open, onToggle, testid, active, children }) => (
  <div>
    <button
      onClick={onToggle}
      className={cn('nav-item w-full justify-between gap-2', active && 'bg-muted/50 text-primary')}
      data-testid={testid}
      title={label}
    >
      <div className="flex items-center gap-2 min-w-0">
        {Icon && <Icon className="h-4 w-4 shrink-0" />}
        <span className="rail-text whitespace-nowrap font-semibold">{label}</span>
      </div>
      <ChevronRight className={cn('rail-text h-4 w-4 shrink-0 transition-transform', open && 'rotate-90')} />
    </button>
    {open && <div className="space-y-1">{children}</div>}
  </div>
);

export default function MainLayout() {
  const { user, logout, hasPermission } = useAuth();
  const { styling } = useStyling();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // 1024–1279px: icon strip unless pinned open (remembered on this browser).
  const [navPinned, setNavPinned] = useState(() => localStorage.getItem('hal-nav-pinned') === '1');
  const togglePinned = () => setNavPinned((v) => { localStorage.setItem('hal-nav-pinned', v ? '0' : '1'); return !v; });

  const inSettings = isSettingsPath(location.pathname);

  // Remember the last non-settings location so "Back to app" can return there.
  const lastAppPathRef = useRef('/');
  useEffect(() => {
    if (!inSettings) lastAppPathRef.current = location.pathname + location.search;
  }, [location.pathname, location.search, inSettings]);

  const [mainOpen, setMainOpen] = useState(() =>
    loadOpen('hal-nav-main-open', Object.fromEntries(mainGroups.map((g) => [g.key, g.defaultOpen])))
  );
  const [settingsOpen, setSettingsOpen] = useState(() =>
    loadOpen('hal-nav-settings-open', Object.fromEntries(settingsCategories.map((c) => [c.key, false])))
  );

  const toggleMain = (key) => setMainOpen((prev) => {
    const next = { ...prev, [key]: !prev[key] };
    saveOpen('hal-nav-main-open', next);
    return next;
  });
  const toggleSettings = (key) => setSettingsOpen((prev) => {
    const next = { ...prev, [key]: !prev[key] };
    saveOpen('hal-nav-settings-open', next);
    return next;
  });

  // When landing on a Settings page directly (saved link / refresh / typed URL),
  // auto-expand the category that owns the active route so the user can see
  // where they are in the menu.
  useEffect(() => {
    if (!inSettings) return;
    const path = location.pathname;
    const owning = settingsCategories.find((cat) =>
      cat.items.some((item) => {
        const base = item.to.split('?')[0];
        return path === base || path.startsWith(base + '/');
      })
    );
    if (owning) {
      setSettingsOpen((prev) => {
        if (prev[owning.key]) return prev;
        const next = { ...prev, [owning.key]: true };
        saveOpen('hal-nav-settings-open', next);
        return next;
      });
    }
  }, [location.pathname, inSettings]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };
  const closeSidebar = () => setSidebarOpen(false);
  const backToApp = () => { navigate(lastAppPathRef.current || '/'); closeSidebar(); };

  // ─── Permission helpers ───────────────────────────────
  const itemVisible = (item) => {
    if (item.masterOr) {
      if (hasPermission('admin', 'manageIntegrations') || hasPermission('admin', 'systemSettings')) return true;
    }
    if (item.gate) return hasPermission(item.gate.resource, item.gate.action);
    if (item.gateAny) return item.gateAny.some((g) => hasPermission(g.resource, g.action));
    return true;
  };
  const groupGateOk = (g) => {
    if (g.gate) return hasPermission(g.gate.resource, g.gate.action);
    if (g.gateAny) return g.gateAny.some((x) => hasPermission(x.resource, x.action));
    return true;
  };

  const stripItem = (item) => {
    const { gate, gateAny, masterOr, ...navProps } = item;
    return navProps;
  };

  const reportsVisible = hasPermission('reports', 'view') || hasPermission('reports', 'viewOperational');

  const visibleSettingsCats = settingsCategories.filter((c) => groupGateOk(c) && c.items.some(itemVisible));
  // Settings destination is reachable if the user can see at least one settings item.
  const canSeeSettings = visibleSettingsCats.length > 0;
  const allSettingsOpen = visibleSettingsCats.length > 0 && visibleSettingsCats.every((c) => settingsOpen[c.key]);
  const toggleAllSettings = () => {
    const target = !allSettingsOpen;
    const next = { ...settingsOpen };
    settingsCategories.forEach((c) => { next[c.key] = target; });
    setSettingsOpen(next);
    saveOpen('hal-nav-settings-open', next);
  };

  const navTitle = styling?.navTitle || '';
  const initials = (user?.name || '?').split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div className="min-h-screen bg-background flex">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}
      {/* Holds the icon strip's space at 1024–1279px (see .hal-rail in index.css). */}
      {!navPinned && <div className="hal-rail-spacer hidden" aria-hidden />}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-56 bg-background border-r border-border flex flex-col',
          'transform transition-transform duration-200 ease-in-out lg:translate-x-0 lg:sticky lg:top-0 lg:h-screen',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
          !navPinned && 'hal-rail'
        )}
        data-testid="app-sidebar"
        data-pinned={navPinned ? 'true' : 'false'}
      >
        {/* Logo - fixed at top */}
        <div className="rail-header h-14 flex items-center justify-between gap-2 px-3 border-b border-border flex-shrink-0">
          <div className="rail-text flex items-center gap-2 min-w-0">
            <span className="text-lg font-bold tracking-tight text-foreground cursor-default" title={`Version ${APP_VERSION}`} data-testid="app-title">{navTitle}</span>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="rail-text contents"><ConnectionStatusIndicator /></span>
            <span className="rail-text contents"><SystemStatusIndicator /></span>
            <button
              onClick={() => { navigate('/search'); closeSidebar(); }}
              className="rail-text p-1 hover:bg-muted rounded text-muted-foreground hover:text-foreground transition-colors"
              title="Search"
              aria-label="Search"
              data-testid="topbar-search-btn"
            >
              <Search className="h-5 w-5" />
            </button>
            <NotificationBell />
            <button
              onClick={togglePinned}
              className="rail-text hidden lg:inline-flex xl:hidden p-1 hover:bg-muted rounded text-muted-foreground hover:text-foreground transition-colors"
              title={navPinned ? 'Unpin menu (shrink to icons)' : 'Pin menu open'}
              aria-label={navPinned ? 'Unpin menu' : 'Pin menu open'}
              data-testid="nav-pin-btn"
            >
              {navPinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
            </button>
            <button className="lg:hidden p-1 hover:bg-muted rounded" onClick={() => setSidebarOpen(false)}>
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Navigation - scrollable middle section */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto min-h-0">
          {inSettings ? (
            <>
              {/* Back to app — sits where the main nav header would be */}
              <button onClick={backToApp} className="nav-item w-full font-semibold text-foreground" data-testid="nav-back-to-app" title="Back to app">
                <ArrowLeft className="h-4 w-4" />
                <span className="rail-text">Back to app</span>
              </button>

              {/* Settings heading + expand/collapse all */}
              <div className="rail-text flex items-center justify-between pl-3 pr-2 py-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Settings</span>
                {visibleSettingsCats.length > 0 && (
                  <button
                    onClick={toggleAllSettings}
                    className="text-[11px] font-medium text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="settings-expand-collapse-all"
                  >
                    {allSettingsOpen ? 'Collapse all' : 'Expand all'}
                  </button>
                )}
              </div>

              {visibleSettingsCats.map((cat) => (
                <NavGroup
                  key={cat.key}
                  icon={cat.icon}
                  label={cat.label}
                  open={!!settingsOpen[cat.key]}
                  onToggle={() => toggleSettings(cat.key)}
                  testid={`nav-settings-cat-${cat.key}`}
                >
                  {cat.items.filter(itemVisible).map((item) => (
                    <NavItem key={item.to} {...stripItem(item)} indent onClick={closeSidebar} />
                  ))}
                </NavGroup>
              ))}
            </>
          ) : (
            <>
              {/* Dashboard — standalone top-level item (above the groups) */}
              <NavItem to="/" icon={LayoutDashboard} label="Dashboard" exact onClick={closeSidebar} />

              {mainGroups.map((group) => {
                if (!groupGateOk(group)) return null;
                const visibleItems = group.items.filter(itemVisible);
                if (visibleItems.length === 0) return null;
                const active = visibleItems.some((it) => {
                  const base = it.to.split('?')[0];
                  return base !== '/' && location.pathname.startsWith(base);
                });
                return (
                  <NavGroup
                    key={group.key}
                    icon={group.icon}
                    label={group.label}
                    open={!!mainOpen[group.key]}
                    onToggle={() => toggleMain(group.key)}
                    testid={`nav-group-${group.key}`}
                    active={active && !mainOpen[group.key]} // highlight the group header only when collapsed
                  >
                    {visibleItems.map((item) => (
                      <NavItem key={item.to} {...stripItem(item)} indent onClick={closeSidebar} />
                    ))}
                  </NavGroup>
                );
              })}

              {/* Reports — standalone top-level item */}
              {reportsVisible && (
                <NavItem to="/reports" icon={BarChart3} label="Reports" onClick={closeSidebar} />
              )}
            </>
          )}
        </nav>

        {/* Footer - pinned to bottom */}
        <div className="mt-auto p-3 border-t border-border flex-shrink-0 bg-background space-y-1">
          {canSeeSettings && (
            <button
              onClick={() => { navigate('/settings'); closeSidebar(); }}
              className={cn('nav-item w-full', inSettings && 'active')}
              data-testid="footer-settings"
              title="Settings"
            >
              <Settings className="h-4 w-4" />
              <span className="rail-text">Settings</span>
            </button>
          )}
          <NavItem to="/settings/changelog" icon={ScrollText} label="What's New" onClick={closeSidebar} />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="nav-item w-full flex items-center gap-3 px-3 py-2 rounded-sm hover:bg-muted transition-colors mt-1"
                data-testid="user-menu-trigger"
                title={user?.name}
              >
                <div className="h-8 w-8 shrink-0 rounded-full bg-primary/20 flex items-center justify-center text-xs font-semibold text-primary">
                  {initials}
                </div>
                <div className="rail-text flex-1 min-w-0 text-left">
                  <div className="text-sm font-medium truncate">{user?.name}</div>
                  <div className="text-xs text-muted-foreground capitalize">{user?.role}</div>
                </div>
                <ChevronDown className="rail-text h-4 w-4 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="top" className="w-48">
              <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="user-menu-profile">
                <User className="mr-2 h-4 w-4" />
                Profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/notifications')} data-testid="user-menu-notifications">
                <Bell className="mr-2 h-4 w-4" />
                Notifications
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={(e) => { e.preventDefault(); toggleTheme(); }}
                data-testid="theme-toggle"
              >
                {theme === 'dark' ? <Sun className="mr-2 h-4 w-4" /> : <Moon className="mr-2 h-4 w-4" />}
                {theme === 'dark' ? 'Light mode' : 'Dark mode'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="mr-2 h-4 w-4" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 glass-header flex items-center justify-between px-4 lg:hidden">
          <button className="p-2 hover:bg-muted rounded" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-btn">
            <Menu className="h-5 w-5" />
          </button>
          <span className="text-xl font-black tracking-tight text-foreground" data-testid="mobile-app-title">{navTitle}</span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => navigate('/search')}
              className="p-2 hover:bg-muted rounded text-muted-foreground hover:text-foreground transition-colors"
              title="Search"
              aria-label="Search"
              data-testid="mobile-topbar-search-btn"
            >
              <Search className="h-5 w-5" />
            </button>
            <NotificationBell testId="notification-bell-mobile" />
          </div>
        </header>

        {/* Padding is a layout concern, not a page concern — pages own space-y-6 only. */}
        <main
          className={`flex-1 overflow-auto bg-[#F8F9FA] dark:bg-background ${
            isFullBleedRoute(location.pathname) ? '' : 'p-6'
          }`}
          data-testid="app-main"
        >
          {/* The page's own code arrives on demand, so the wait belongs here rather
              than around <Routes>: a cold load spins inside the content area and the
              sidebar and header stay on screen. Clicking around inside the app does not
              paint this at all — React keeps the current page until the next one is
              ready. See components/shared/RouteFallback.jsx. */}
          <Suspense fallback={<RouteFallback />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
    </div>
  );
}
